import {
    PlusJakartaSans_300Light,
    PlusJakartaSans_400Regular,
    PlusJakartaSans_500Medium,
    PlusJakartaSans_600SemiBold,
    PlusJakartaSans_700Bold,
    PlusJakartaSans_800ExtraBold
} from "@expo-google-fonts/plus-jakarta-sans";
import { useFonts } from "expo-font";
import * as Linking from "expo-linking";
import { Stack, useRouter, useSegments } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import {
    ActivityIndicator,
    Alert,
    AppState,
    View,
    Text,
    TouchableOpacity,
    StyleSheet
} from "react-native";
import { PaperProvider } from "react-native-paper";

import { AppLockGate } from "@/components/app-lock/AppLockGate";
import { AppUpdateGate } from "@/components/app-update/AppUpdateGate";
import { FormPreviewHost } from "@/components/form-preview/FormPreviewHost";
import { LiveTranslationRecordingPill } from "@/components/visit/LiveTranslationRecordingPill";
import { WebRetiredScreen } from "@/components/web-retired/WebRetiredGate";
import { ApiError } from "@/lib/api";
import { loadApiEnv } from "@/lib/api-env";
import { AppLockProvider } from "@/lib/app-lock-context";
import { AuthProvider, useAuth } from "@/lib/auth";
import { devToolsEnabled } from "@/lib/build-channel";
import { MOBILE_DEBUG_VERSION } from "@/lib/debug";
import { isFormPreviewRequested } from "@/lib/form-preview";
import { LiveTranslationProvider } from "@/lib/live-translation/LiveTranslationProvider";
import { initLogRocket, identifyLogRocket } from "@/lib/logrocket";
import { NotificationProvider } from "@/lib/notifications";
import { PushNotificationProvider } from "@/lib/push-notifications";
import { Recorder } from "@/lib/recorder";
import {
    addSentryBreadcrumb,
    captureSentryMessage,
    initSentry,
    setSentryUser,
    wrapRoot
} from "@/lib/sentry-init";
import { triggerSentryTest, type SentryTestKind } from "@/lib/sentry-test";
import { paperTheme, colors } from "@/lib/theme";
import { VisitLocationTrail } from "@/lib/visit-location-trail";
import { hasStoredBypass, isWebRetiredEnforced } from "@/lib/web-retired";
import { WebSocketProvider } from "@/lib/websocket";

const sentryReady = initSentry();
if (sentryReady) {
    installConsoleForwarder();
    addSentryBreadcrumb({ category: "app", level: "info", message: "[sentry] init ok" });
    const dsnHost =
        process.env.EXPO_PUBLIC_SENTRY_DSN?.split("@")[1]?.split("/")[0] ?? "unknown-host";
    console.warn(`[sentry] up — ${dsnHost}`);
} else {
    console.warn("[sentry] down — EXPO_PUBLIC_SENTRY_DSN not set");
}

// Visit Recording — shim Alert.alert to emit alert_shown events. Recorder is
// statically imported (line 30); the previous dynamic import was dead code
// since the static import already succeeds on this load path. Recorder.addEvent
// is a no-op when no session is active, so calls outside an active visit are
// safe.
{
    const originalAlert = Alert.alert.bind(Alert);
    Alert.alert = ((
        title: string,
        message?: string,
        buttons?: Parameters<typeof Alert.alert>[2],
        opts?: Parameters<typeof Alert.alert>[3]
    ) => {
        try {
            Recorder.addEvent("alert_shown", {
                title,
                message: typeof message === "string" ? message : undefined
            });
        } catch {
            /* shim must never break Alert */
        }
        return originalAlert(title, message, buttons, opts);
    }) as typeof Alert.alert;
}

// Forward console.warn/error to Sentry: warns become breadcrumbs, errors
// become captured events. Runs once at module init after Sentry.init.
function installConsoleForwarder() {
    const origWarn = console.warn;
    const origError = console.error;
    console.warn = (...args: unknown[]) => {
        addSentryBreadcrumb({
            category: "console",
            level: "warning",
            message: args.map(String).join(" ")
        });
        origWarn(...args);
    };
    console.error = (...args: unknown[]) => {
        // Aborted requests (user navigated away mid-fetch) and raw network
        // failures (offline, DNS blip, TLS hiccup — anything asApiErrorFromCatch
        // in lib/api.ts couldn't attribute to the server) are both expected
        // client-side conditions, never an app bug. Log them to the console for
        // local debugging but don't ship them to Sentry, or they pile up as
        // phantom error issues (e.g. "Failed to refresh section state" —
        // TASKHEALTH-MOBILE-1K — was only catching the abort kind before).
        const isExpectedNetworkNoise = args.some(
            (a) => a instanceof ApiError && (a.kind === "abort" || a.kind === "network")
        );
        if (isExpectedNetworkNoise) {
            origError(...args);
            return;
        }
        const message = args.map(String).join(" ");
        addSentryBreadcrumb({ category: "console", level: "error", message });
        captureSentryMessage(message, "error");
        origError(...args);
    };
}

function handleSentryTestUrl(url: string) {
    // `taskhealth://sentry-test?kind=native` crashes the app on purpose. Any web
    // page or text message can open that link, so the production app ignores
    // it; dev sessions and preview builds keep it for checking Sentry.
    if (!devToolsEnabled()) {
        return;
    }
    const parsed = Linking.parse(url);
    if (parsed.hostname !== "sentry-test" && parsed.path !== "sentry-test") {
        return;
    }
    const kind = parsed.queryParams?.kind;
    if (kind === "message" || kind === "error" || kind === "native") {
        triggerSentryTest(kind as SentryTestKind);
    }
}

function RootNavigator() {
    const { user, isLoading, authStatus, pendingPhone } = useAuth();
    const segments = useSegments();
    const router = useRouter();

    useEffect(() => {
        if (user?.rnId) {
            try {
                identifyLogRocket(String(user.rnId));
                console.warn("[logrocket] user identity set:", user.rnId);
            } catch (e) {
                console.warn("[logrocket] identify failed:", e);
            }
            setSentryUser(String(user.rnId));
        }
    }, [user?.rnId]);

    useEffect(() => {
        if (isLoading) {
            return;
        }

        const inAuthGroup = segments[0] === "(auth)";
        const inOnboarding = segments[0] === "onboarding";
        const newUserScreens = ["account-choice", "add-number", "register"];
        const inNewUserFlow =
            inAuthGroup && newUserScreens.includes((segments as string[])[1] ?? "");

        if (authStatus === "new_user" && !inNewUserFlow) {
            // New user: choose to start onboarding, or add this number to an
            // existing account (routes onward to register / add-number).
            router.replace({
                pathname: "/(auth)/account-choice",
                params: { phone: pendingPhone ?? "" }
            });
        } else if (!user && authStatus !== "new_user" && !inAuthGroup && !inOnboarding) {
            // Not authenticated, not new user — go to welcome
            router.replace("/(auth)/welcome");
        } else if (
            user &&
            (authStatus === "active" || authStatus === "pending_application") &&
            inAuthGroup
        ) {
            // Authenticated — leave auth group
            router.replace("/(tabs)");
        }
    }, [user, isLoading, authStatus, segments]);

    if (isLoading) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.teal400} />
            </View>
        );
    }

    return (
        <Stack
            screenOptions={{
                headerStyle: { backgroundColor: colors.background },
                headerTintColor: colors.textPrimary,
                headerTitleStyle: { fontWeight: "bold" as const },
                headerShadowVisible: false,
                animation: "fade"
            }}
        >
            <Stack.Screen name="(auth)" options={{ headerShown: false }} />
            <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
            <Stack.Screen name="available-visit/[id]" options={{ headerShown: false }} />
            <Stack.Screen name="suggestions" options={{ headerShown: false }} />
            <Stack.Screen name="visit/[id]" options={{ headerShown: false }} />
            <Stack.Screen name="chat/[conversationId]" options={{ headerShown: false }} />
            <Stack.Screen name="onboarding" options={{ headerShown: false }} />
            <Stack.Screen name="compliance" options={{ headerShown: false }} />
            <Stack.Screen name="training" options={{ headerShown: false }} />
            <Stack.Screen name="id-card" options={{ headerShown: false }} />
            <Stack.Screen name="phones" options={{ headerShown: false }} />
            <Stack.Screen name="payment-statement" options={{ headerShown: false }} />
        </Stack>
    );
}

function RootLayout() {
    const [fontsLoaded] = useFonts({
        PlusJakartaSans_300Light,
        PlusJakartaSans_400Regular,
        PlusJakartaSans_500Medium,
        PlusJakartaSans_600SemiBold,
        PlusJakartaSans_700Bold,
        PlusJakartaSans_800ExtraBold
    });

    // Retired web build. Resolved once on mount, never re-read: a nurse cannot
    // become un-blocked except through the 5-tap bypass, which sets this state
    // directly.
    //  Resolved once on mount from the URL, never re-read: the admin iframe
    //  sets the flag when it loads the frame and never navigates it away.
    const [formPreview] = useState(() => isFormPreviewRequested(globalThis.location?.search));

    const [webRetired, setWebRetired] = useState(
        () => isWebRetiredEnforced() && !hasStoredBypass()
    );
    const dismissWebRetired = useCallback(() => setWebRetired(false), []);

    useEffect(() => {
        loadApiEnv();
        console.info(`[mobile-start ${MOBILE_DEBUG_VERSION}] app boot`);
        try {
            initLogRocket();
            console.warn("[logrocket] initialized");
        } catch (e) {
            console.warn("[logrocket] init failed:", e);
        }

        Linking.getInitialURL().then((url) => {
            if (url) {
                handleSentryTestUrl(url);
            }
        });
        const sub = Linking.addEventListener("url", (event) => {
            handleSentryTestUrl(event.url);
        });

        // Visit Recording — emit lifecycle events so the player can
        // distinguish backgrounded periods from active capture.
        //
        // The visit location trail listens to the same two transitions, but
        // stores them itself rather than through the Recorder: recording ingest
        // has been server-side OFF since 2026-08-04, so these Recorder events
        // currently go nowhere, and the trail has to work regardless.
        const appStateSub = AppState.addEventListener("change", (next) => {
            if (next === "background") {
                Recorder.addEvent("app_background", {});
                void VisitLocationTrail.onAppStateChange("background");
            } else if (next === "active") {
                Recorder.addEvent("app_foreground", {});
                void VisitLocationTrail.onAppStateChange("active");
            }
        });

        // Visit Recording — global heartbeat. Single 30s interval keyed on
        // Recorder.state().started, NOT on a screen prop. Survives navigation
        // between visit subroutes (index → assessment → section → ...). The
        // orphan-cleanup cron uses last_heartbeat_at to detect dead sessions;
        // pinning the heartbeat to one screen made every navigation a 30s
        // gap large enough to make the cron wrong-by-default.
        const heartbeatHandle = setInterval(() => {
            const s = Recorder.state();
            if (s.started && s.sessionId) {
                void Recorder.heartbeat({}).catch(() => {
                    /* swallow — best-effort */
                });
            }
        }, 30_000);

        // Visit location trail — global ticker, same reasoning as the heartbeat
        // above: it must survive navigation between the visit subroutes, which
        // is where the nurse actually spends the visit.
        //
        // It ticks every 30s rather than every 3 minutes on purpose. The ticker
        // decides "has enough time passed since the last reading", which stays
        // correct when the OS throttles or suspends our timers; a 3-minute
        // interval would silently drift or skip.
        const trailHandle = setInterval(() => {
            void VisitLocationTrail.tick(AppState.currentState === "active");
        }, 30_000);

        return () => {
            sub.remove();
            appStateSub.remove();
            clearInterval(heartbeatHandle);
            clearInterval(trailHandle);
        };
    }, []);

    if (!fontsLoaded) {
        return (
            <View style={styles.loadingContainer} testID="tricanary-mobile-20260530">
                <ActivityIndicator size="large" color={colors.teal400} />
            </View>
        );
    }

    //  Admin Form Viewer's device pane. Before the providers AND before the
    //  retired-web gate, deliberately: a preview holds no session, opens no
    //  WebSocket, registers no push token, and is not a nurse using the PWA,
    //  so neither the provider stack nor the store redirect applies to it.
    //  Placed after the font gate for the same reason the block below is —
    //  so it draws in Plus Jakarta rather than flashing a system fallback.
    if (formPreview) {
        return <FormPreviewHost />;
    }

    //  Before the providers, deliberately. Returning here means a blocked
    //  browser never mounts AuthProvider, never opens the WebSocket, and never
    //  attempts push registration — so it holds no session and does no work
    //  behind the block. Placed after the font gate so the screen renders in
    //  Plus Jakarta rather than flashing a system fallback.
    if (webRetired) {
        return <WebRetiredScreen onBypass={dismissWebRetired} />;
    }

    return (
        <View style={styles.flex}>
            <PaperProvider theme={paperTheme}>
                <AuthProvider>
                    <AppLockProvider>
                        <WebSocketProvider>
                            <NotificationProvider>
                                <PushNotificationProvider>
                                    <LiveTranslationProvider>
                                        <RootNavigator />
                                        <LiveTranslationRecordingPill />
                                        {__DEV__ && <DevBadge />}
                                        {/* Last child so it covers everything
                                            above, including the recording pill. */}
                                        <AppLockGate />
                                        {/* Above the lock: an app that must be
                                            updated from the store need not be
                                            unlocked first. */}
                                        <AppUpdateGate />
                                    </LiveTranslationProvider>
                                </PushNotificationProvider>
                            </NotificationProvider>
                        </WebSocketProvider>
                    </AppLockProvider>
                </AuthProvider>
            </PaperProvider>
        </View>
    );
}

export default wrapRoot(RootLayout);

function DevBadge() {
    const { logout } = useAuth();
    return (
        <TouchableOpacity style={styles.devBadge} onPress={logout} activeOpacity={0.6}>
            <Text style={styles.devBadgeText}>LOCAL</Text>
        </TouchableOpacity>
    );
}

const styles = StyleSheet.create({
    flex: {
        flex: 1
    },
    loadingContainer: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: colors.background
    },
    devBadge: {
        position: "absolute",
        top: 55,
        right: 8,
        backgroundColor: "#F5A623",
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 4,
        zIndex: 9999,
        opacity: 0.85
    },
    devBadgeText: {
        color: "#000",
        fontSize: 10,
        fontWeight: "700",
        letterSpacing: 0.5
    }
});
